import React, {useEffect, useState} from "react";
import {composeActionName} from "hooks/show/stand/task/historyTimeline/useMapTimeline";
import {formatISODate} from "tools/formats/formatDate";

import {ITaskTimeline, Roles} from "@evennt-tech/api-v2-core";
import {UnpackedAction} from "api/shows/stands/tasks/timeline";
import unpackAction from "api/shows/stands/tasks/timeline/functions/unpackAction";
import {useLocalMapContext} from "view/context/LocalMapContext";
import {T, useTranslate} from "@tolgee/react";
import useDateFnsLocale from "hooks/useDateFnsLocale";

function LastActivity({
  lastAction,
  dateFormat,
  standId,
  showId,
}: {
  lastAction?: ITaskTimeline | null;
  dateFormat: string;
  // Passed to `unpackAction` so the exhibitor-connection lookup resolves
  // company / logo. `standId` is the stand UUID (`stands.id`).
  standId?: string;
  showId?: string;
}) {
  const localMap = useLocalMapContext();
  const {t} = useTranslate();
  const locale = useDateFnsLocale();
  const [unpackedAction, setUnpackedAction] = useState<UnpackedAction | null>(
    null
  );

  useEffect(() => {
    const loader = async () => {
      if (!lastAction) {
        setUnpackedAction(null);
        return;
      }
      const isValidEmailAction =
        lastAction.role === Roles.EMAIL && !!lastAction.email;
      if (isValidEmailAction) {
        setUnpackedAction({
          role: Roles.EMAIL,
          createdAt: lastAction.createdAt,
          companyName: lastAction.email as string,
          // Show the email reviewer's identity ("by <email>").
          producedBy: (lastAction.producedBy || lastAction.email) as string,
        });
        return;
      }

      const result = await unpackAction(lastAction, localMap, {
        showId,
        standId,
      });
      setUnpackedAction(result);
    };

    loader();
  }, [lastAction, showId, standId, localMap]);

  if (!unpackedAction || !lastAction) {
    return <T keyName="common.tasks.history.no_history" />;
  }

  return (
    <>
      {t("common.tasks.history.action_by", {
        action: composeActionName(lastAction, t),
        user:
          unpackedAction.producedBy || t("common.tasks.multi_reviewer.user"),
      })}
      <br />
      {formatISODate(unpackedAction.createdAt, dateFormat, locale)}
    </>
  );
}

export default LastActivity;
